import React, { useRef, useEffect, useState } from 'react';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';
import BalanceWidget from '../../widgets/BalanceWidget/BalanceWidget';
import { navigation, STATIC_PATH } from '../../config';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import { FormattedMessage } from 'react-intl';
import SVG from 'react-inlinesvg';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import PersonalManagerWidget from '../../widgets/PersonalManagerWidget/PersonalManagerWidget';
import BannerWidget from '../../widgets/BannerWidget/BannerWidget';
import { startLogout } from '../../actions/webmaster';
import ThemeToggle from './ThemeToggle';
import { DARK_THEME, getEffectiveTheme } from '../../utils/themeStorage';

const SideBar = ({ closeModal }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { balance, profile, unreadTicketsCount, openMenu, globalSettings } = useSelector(
    (state) => ({
      balance: state.webmaster.balance,
      profile: state.webmaster.profile,
      unreadTicketsCount: state.webmaster.unreadTicketsCount,
      openMenu: state.webmaster.tour.openMenu,
      globalSettings: state.app.globalSettings,
    }),
  );
  const [itemId, setItemId] = useState(null);
  const sidebarRef = useRef(null);
  const navbarTogglerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const { banned: isUserBlocked } = profile;
  const sidebarClasses = cn('sidebar-main', { isOpen });
  const hasNotificationClass = cn({ 'has-notification': unreadTicketsCount > 0 });
  const effectiveTheme = getEffectiveTheme(profile.theme);
  const profileImgSrc = profile.img
    ? `${profile.img}?${Date.now()}`
    : `${STATIC_PATH}/img/action-icons/user.svg`;

  useEffect(() => {
    const cleanPath = (path) => {
      if (!path || typeof path !== 'string') return '';
      return path.split('?')[0];
    };

    const activeNavItem = navigation.find((item) => {
      if (item.subnav && Object.keys(item.subnav).length > 0) {
        return Object.values(item.subnav).some((subItem) => {
          const cleanSubItemPath = cleanPath(subItem.path);
          return cleanSubItemPath && location.pathname === cleanSubItemPath;
        });
      } else {
        const cleanItemPath = cleanPath(item.path);
        const cleanItemIdPath = cleanPath(`/${item.id}`);
        return (
          (cleanItemPath && location.pathname === cleanItemPath) ||
          location.pathname === cleanItemIdPath
        );
      }
    });

    setItemId(activeNavItem ? activeNavItem.id : null);
  }, [location.pathname]);

  useEffect(() => {
    const handleDocumentClick = (e) => {
      const sidebar = sidebarRef.current;
      const toggler = navbarTogglerRef.current;

      if (!sidebar || sidebar.contains(e.target) || (toggler && toggler.contains(e.target))) {
        return;
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    document.addEventListener('touchstart', handleDocumentClick);

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
      document.removeEventListener('touchstart', handleDocumentClick);
    };
  }, []);

  const toggleAccordion = (id) => {
    setItemId((prevId) => (prevId === id ? null : id));
  };

  const handleNavItemClick = () => {
    closeModal && closeModal();
  };

  const handleLogoutClick = () => {
    closeModal && closeModal();
    dispatch(startLogout());
  };

  return (
    <ErrorBoundary>
      <aside className={sidebarClasses} ref={sidebarRef} id="sidebar">
        <div>
          <NavLink className="sidebar-logo" to="/" onClick={handleNavItemClick}>
            <img
              src={`${STATIC_PATH}/img/logo-${effectiveTheme === DARK_THEME ? 'w' : 'b'}.svg`}
              alt="TutorPlace"
              width="148"
              height="29"
            />
            <span>Партнерская программа</span>
          </NavLink>

          {navigation.map((item) => {
            const isActive = itemId === item.id;

            return (
              <div key={item.id}>
                {item.id === 'account' && <div className="sidebar-separator" />}
                <div
                  className={cn('sidebar-item', {
                    active: isActive,
                    'sidebar-item-account': item.id === 'account',
                  })}
                >
                  {item.subnav && Object.keys(item.subnav).length > 0 ? (
                    <>
                      <div
                        className={cn('sidebar-link accordion-toggle', {
                          active: isActive,
                        })}
                        onClick={() => toggleAccordion(item.id)}
                      >
                        <div className="sidebar-link-icon-wrapper">
                          <SVG
                            className="sidebar-link-icon"
                            src={`${STATIC_PATH}/img/action-icons/${item.id}.svg`}
                            height="20"
                            width="20"
                            cacheRequests
                          />
                          <SVG
                            className="sidebar-link-icon-select"
                            src={`${STATIC_PATH}/img/action-icons/${item.id}-select.svg`}
                            height="20"
                            width="20"
                            cacheRequests
                          />
                        </div>
                        {item.id === 'account' ? (
                          <>
                            <span className="sidebar-user">
                              {profile.img && (
                                <LazyLoadImage
                                  src={profileImgSrc}
                                  alt="user"
                                  visibleByDefault={true}
                                />
                              )}
                            </span>
                            <span className="webmaster-name">{profile.login}</span>
                          </>
                        ) : (
                          <FormattedMessage id={item.translated} defaultMessage={item.title} />
                        )}
                        {hasNotificationClass && item.id === 'help' && (
                          <span className="sidebar-highlight-circle" />
                        )}
                        <span
                          className={cn('accordion-icon', {
                            open: isActive,
                          })}
                        />
                        <SVG
                          className="sidebar-arrow-icon"
                          src={`${STATIC_PATH}/img/action-icons/arrow.svg`}
                          height="20"
                          width="20"
                          cacheRequests
                        />
                      </div>

                      <div
                        className={cn('subnav-menu', {
                          open: isActive,
                        })}
                      >
                        {Object.values(item.subnav).map((subItem) => {
                          if (subItem.id === 'logout') {
                            return (
                              <NavLink
                                key={subItem.id}
                                to={subItem.path}
                                className="sidebar-link sidebar-sublink"
                                onClick={handleLogoutClick}
                              >
                                <FormattedMessage
                                  id={subItem.translated}
                                  defaultMessage={subItem.title}
                                />
                              </NavLink>
                            );
                          }
                          return (
                            <NavLink
                              key={subItem.id}
                              to={subItem.path}
                              className="sidebar-link sidebar-sublink"
                              onClick={handleNavItemClick}
                            >
                              <FormattedMessage
                                id={subItem.translated}
                                defaultMessage={subItem.title}
                              />
                              {subItem.id === 'support' && (
                                <span className="sidebar-notification">{unreadTicketsCount}</span>
                              )}
                            </NavLink>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <NavLink
                      to={item.path || `/${item.id}`}
                      className="sidebar-link"
                      onClick={handleNavItemClick}
                    >
                      <div className="sidebar-link-icon-wrapper">
                        <SVG
                          className="sidebar-link-icon"
                          src={`${STATIC_PATH}/img/action-icons/${item.id}.svg`}
                          height="20"
                          width="20"
                          cacheRequests
                        />
                        <SVG
                          className="sidebar-link-icon-select"
                          src={`${STATIC_PATH}/img/action-icons/${item.id}-select.svg`}
                          height="20"
                          width="20"
                          cacheRequests
                        />
                      </div>
                      <FormattedMessage id={item.translated} defaultMessage={item.title} />
                    </NavLink>
                  )}
                </div>
              </div>
            );
          })}

          <div className="sidebar-separator" />
          <ThemeToggle />
        </div>

        {!isUserBlocked && <BalanceWidget balance={balance} />}

        {/* <PersonalManagerWidget /> */}

        <BannerWidget />
      </aside>
    </ErrorBoundary>
  );
};

export default SideBar;
